import React from "react";

import {
  FetchNotFoundError,
  FetchServerError,
  FetchUnauthorizedError,
  FetchPasswordInvalidError,
  FetchSsoError,
  FetchElevatedAccessError,
} from "@lib/errors";
import { FetchUserError } from "@lib/errors/FetchUserError";
import { UNIDENTIFIED_ERROR } from "@lib/fetch";

import useI18nNamespace from "@services/useI18nNamespace";

import type { FetchErrorModalTitleProps } from "./types";

export const FetchErrorModalTitle = ({ error }: FetchErrorModalTitleProps): React.ReactElement => {
  const { t } = useI18nNamespace("server-fetch-error-messages");
  if (error instanceof FetchServerError) {
    switch (error.status) {
      case 503:
      case 504:
        return <>{t("request-timeout-title", "Request Timed Out")}</>;
      case 500:
      default:
        return <>{t("server-error-title", "Unexpected Error")}</>;
    }
  }
  if (error instanceof FetchElevatedAccessError) {
    return <>{t("fetch-elevated-access-error-title", "Reauthentication Required")}</>;
  }
  if (error instanceof FetchUserError) {
    return <>{error.responseJson.warn_header}</>;
  }
  if (error instanceof FetchNotFoundError) {
    return <>{t("fetch-not-found-title", "Page Not Found")}</>;
  }
  if (error instanceof FetchUnauthorizedError) {
    return <>{t("unauthorized-title", "Insufficient Permissions")}</>;
  }
  if (error instanceof FetchPasswordInvalidError) {
    return <>{t("password-expired-or-invalid-title", "Must Change Password")}</>;
  }
  if (error instanceof FetchSsoError) {
    return <>{t("sso-title", "SSO Authentication Now Required")}</>;
  }
  if (error.status === UNIDENTIFIED_ERROR) {
    return <>{t("unidentified-error-title", "Please Refresh Page")}</>;
  }
  return <>{t("server-error-title", "Unexpected Error")}</>;
};
